import * as mg from 'maplibre-gl'

import { point_to_tile } from 'shared/utils'

type XY = { x: number; y: number }
type onChange = (act: 'add' | 'del', start: XY, end: XY) => void

export class BoxSelect {
    #map: mg.Map
    #box: HTMLDivElement
    #con: HTMLElement
    #act: null | 'add' | 'del'
    #start: [XY, mg.Point] | null
    #end: [XY, mg.Point] | null
    #onchange: onChange
    #enabled: boolean

    constructor(map: mg.Map, onchange: onChange) {
        this.#map = map
        this.#con = map.getContainer()
        this.#box = document.createElement('div')
        this.#con.appendChild(this.#box)
        this.#box.className = 'box-select'
        this.#start = null
        this.#end = null
        this.#act = null
        this.#onchange = onchange
        this.#enabled = true

        this.#map.on('mousedown', this.mousedown.bind(this))
        this.#map.on('mousemove', this.mousemove.bind(this))

        document.addEventListener('mouseup', this.mouseup.bind(this))
        document.addEventListener('keydown', this.keydown.bind(this))
        document.addEventListener('keyup', this.keyup.bind(this))
    }

    enabled(enabled: boolean) {
        this.#enabled = enabled
        if (!this.#enabled) {
            this.#start = null
            this.#end = null
            this.box_update()
        }
        this.check_enabled()
    }

    clean() {
        this.#map.off('mousedown', this.mousedown.bind(this))
        this.#map.off('mousemove', this.mousemove.bind(this))

        document.removeEventListener('mouseup', this.mouseup.bind(this))
        document.removeEventListener('keydown', this.keydown.bind(this))
        document.removeEventListener('keyup', this.keyup.bind(this))

        this.#box.remove()
    }

    private check_enabled() {
        return this.#enabled
    }

    private act(ev: KeyboardEvent | MouseEvent) {
        if (!this.check_enabled()) return

        this.#act = null
        if (ev.shiftKey) {
            this.#act = 'add'
        }
        if (ev.ctrlKey) {
            this.#act = 'del'
        }
        if (!this.#act) {
            this.#start = null
            this.#end = null
        }
        this.box_update()
    }

    private keydown(ev: KeyboardEvent) {
        this.act(ev)
    }
    private keyup(ev: KeyboardEvent) {
        this.act(ev)
    }

    private mousedown(e: mg.MapMouseEvent) {
        if (!this.check_enabled()) return
        if (this.#map.getZoom() < 12) return
        this.act(e.originalEvent)
        if (!this.#act) return

        e.preventDefault()
        let { x, y } = point_to_tile({ ...e.lngLat, zom: 18 })
        this.#start = [{ x, y }, e.point]
        this.#end = [{ x, y }, e.point]
        this.box_update()
    }

    private mousemove(e: mg.MapMouseEvent) {
        if (!this.check_enabled()) return
        this.act(e.originalEvent)
        if (!this.#act || !this.#start) return

        e.preventDefault()
        let { x, y } = point_to_tile({ ...e.lngLat, zom: 18 })
        this.#end = [{ x, y }, e.point]
        this.box_update()
    }
    private mouseup(e: MouseEvent) {
        if (!this.check_enabled()) return
        this.act(e)
        if (!this.#act || !this.#start || !this.#end) return
        e.preventDefault()

        this.#onchange(this.#act, this.#start[0], this.#end[0])
        this.#start = null
        this.#end = null
        this.#act = null
        this.box_update()
    }

    private box_update() {
        if (!this.#enabled || !this.#act || !this.#start || !this.#end) {
            this.#con.classList.remove('maplibregl-crosshair')
            this.#box.style.display = 'none'
            return
        }

        this.#con.classList.add('maplibregl-crosshair')
        this.#box.style.display = ''

        if (this.#act == 'add') {
            this.#box.classList.add('add')
            this.#box.classList.remove('del')
        } else {
            this.#box.classList.add('del')
            this.#box.classList.remove('add')
        }

        let p0 = this.#start[1]
        let p1 = this.#end[1]
        const min_x = Math.min(p0.x, p1.x),
            max_x = Math.max(p0.x, p1.x),
            min_y = Math.min(p0.y, p1.y),
            max_y = Math.max(p0.y, p1.y)

        this.#box.style.transform = `translate(${min_x}px,${min_y}px)`
        this.#box.style.width = `${max_x - min_x}px`
        this.#box.style.height = `${max_y - min_y}px`
    }
}
